import { useMemo, useState } from 'react';
import { PaginationState, SortingState } from '@tanstack/react-table';

import { UseQueryResult } from '@oaknorth/util-rest';

import { mapToSortOrder, SortOrder, TableSorting, useDefaultSortingState } from '../sorting';
import { TableInstance } from '../types';
import { SimpleTableHookProps, useSimpleTable } from '../useSimpleTable';

import { useServerPagination } from './server-pagination';

/**
 * Props for server tables
 * Type parameters:
 * * `TRow` - type of data in a table row
 * * `TData` - type of response from the API
 * * `TError` - type of error response from the API
 */
type ServerTableHookProps<TRow extends object, TData, TError> = Omit<
  SimpleTableHookProps<TRow>,
  'rowData' | 'hasPagination' | 'useManualPagination' | 'useManualSort' | 'hasFilter' | 'useManualFiltering'
> & {
  /**
   * Hook to load the necessary data.
   *
   * Must follow the rules of hooks - call the same hooks inside here each render
   *
   * Receives:
   * * `pagination` - current page number and page size
   * * `sortOrder` - sort order based on column IDs
   * * `globalQuery` - global (all columns) filter query
   *
   * Should return the result from a hook wrapping `useQuery`
   */
  useLoadData: (
    pagination: PaginationState,
    sortOrder: SortOrder,
    globalQuery: string | undefined
  ) => UseQueryResult<TData, TError>;
  /**
   * How to get the list of rows from the API response
   *
   * Avoid transforming the data if you can - operate on the objects from the API
   *
   * **Should be memoised or statically defined outside component scope**
   */
  rowMapper: (data: TData | undefined) => TRow[] | undefined;
  /**
   * How to get the total number of rows (not just the number in this page) from the API response
   */
  getRowCount: (data: TData) => number;

  /**
   * Table sort state manager hook. By default, useDefaultSortingState is used which uses React state,
   * but others can be used such as useURLSorting which uses query parameters for the table sort state
   */
  useSorting?: (initialSortBy?: SortingState) => TableSorting;
};

interface ServerTableHookReturn<TRow extends object, TData, TError> {
  table: TableInstance<TRow>;
  query: UseQueryResult<TData, TError>;
}

/**
 * Hook for handling tables with server-side pagination, filtering and sorting
 * @param options
 * @returns result `{ table: TableData, query: QueryData }`
 */
export function useServerTable<TRow extends object, TData, TError>({
  useLoadData,
  rowMapper,
  getRowCount,
  useSorting = useDefaultSortingState,
  meta,
  ...tableProps
}: ServerTableHookProps<TRow, TData, TError>): ServerTableHookReturn<TRow, TData, TError> {
  // Initialise the state
  const { pageIndex, pageSize, setPagination } = useServerPagination(tableProps.pageSize);
  const { sorting, onSortingChange } = useSorting(tableProps.initialSortBy);
  const [globalQuery, setGlobalQuery] = useState<string | undefined>();

  // Load the data, with the table state provided
  const loadData = useLoadData({ pageIndex, pageSize }, mapToSortOrder(sorting), globalQuery);

  // Map the data to the table formats
  const rowData = useMemo(() => rowMapper(loadData.data), [rowMapper, loadData.data]);

  const rowCount = loadData.data != null ? getRowCount(loadData.data) : null;
  // -1 is react-table's way of saying we don't know the page count - https://tanstack.com/table/v8/docs/api/features/pagination#pagecount
  const pageCount = rowCount != null ? Math.ceil(rowCount / pageSize) : -1;

  const table = useSimpleTable({
    ...tableProps,
    rowData,
    // Pagination and filtering are always enabled
    // but react-table isn't doing anything for it, just passing data back to us to do it server-side
    hasPagination: true,
    hasFilter: true,
    useManualPagination: {
      enabled: true,
      pageCount,
      pageIndex,
      pageSize,
      onPaginationChange: setPagination,
    },
    useManualSort: {
      enabled: true,
      onSetSorting: onSortingChange,
      sorting,
    },
    useManualFiltering: {
      enabled: true,
      onFilterChange: setGlobalQuery,
      filter: globalQuery ?? '',
    },
    meta: { ...meta, response: loadData.data },
  });

  return { table: { ...table, rowCount }, query: loadData };
}
